import { useMemo } from "react";
import { Icon } from "@/components/Icon/Icon";
import { COMPOUND_LIB, FOUNDATION_COMPOUNDS } from "@/services/mock";
import type {
  CompareCatalog,
  CompareDescriptor,
  CompareFormula,
  ComputeCompareStats,
} from "@/utils/types/compare";
import { CMP_COLORS } from "@/utils/types/compare";
import type { FormulationEntry, CompoundLibEntry } from "@/utils/types/compound";
import type { CostBreakdown } from "@/utils/formulation/computeCost";
import { CompareControl } from "./CompareControl";

const ALL_COMPOUNDS = [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS];
const compoundById = (id: string): CompoundLibEntry | undefined =>
  ALL_COMPOUNDS.find((c) => c.id === id);

// ── Column model ─────────────────────────────────────────────────────
// `current` is always one column (centre or right depending on # picks).
// `alt` columns carry the picked formula plus a stable accent colour
// keyed off pick order so they match the radar/scatter overlay legend.
type CurrentCol = {
  kind: "current";
  name: string;
  formulation: FormulationEntry[];
};
type AltCol = {
  kind: "alt";
  id: string;
  name: string;
  descriptor: CompareDescriptor | string;
  source: CompareFormula["source"];
  formulation: FormulationEntry[];
  accent: string;
};
type Col = CurrentCol | AltCol;

const isCurrent = (c: Col): c is CurrentCol => c.kind === "current";

// ── Format helpers ───────────────────────────────────────────────────
function formatPpm(ppm: number): string {
  if (ppm < 1) return ppm.toFixed(2);
  if (ppm < 10) return ppm.toFixed(1);
  return String(Math.round(ppm));
}
function formatDiff(diff: number): string {
  const abs = Math.abs(diff);
  if (abs < 1) return diff.toFixed(2);
  return diff.toFixed(1);
}

// ── Sub-rows ─────────────────────────────────────────────────────────
function ColumnHeader({
  col,
  onClear,
}: {
  col: Col;
  onClear?: () => void;
}) {
  const sourceChip = isCurrent(col)
    ? { label: "Current", className: "current" }
    : col.source === "ai"
    ? { label: "AI", className: "ai" }
    : col.source === "similar"
    ? { label: "Similar", className: "yours" }
    : { label: "Yours", className: "yours" };

  return (
    <div
      className={`cmp-col-header ${isCurrent(col) ? "current" : "alt-accent"}`}
      style={
        isCurrent(col)
          ? undefined
          : ({ ["--cmp-accent" as string]: col.accent } as React.CSSProperties)
      }
    >
      <div
        className="hstack"
        style={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 6,
        }}
      >
        <span className={`cmp-source-chip ${sourceChip.className}`}>
          {sourceChip.label}
        </span>
        {!isCurrent(col) && onClear && (
          <button
            type="button"
            onClick={onClear}
            title="Remove from comparison"
            className="btn xs ghost"
            style={{ padding: 3, flexShrink: 0 }}
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>
      <div
        className="vstack"
        style={{ minWidth: 0, gap: 3, lineHeight: 1.2 }}
      >
        <span
          className="hstack"
          style={{ gap: 6, alignItems: "center", minWidth: 0 }}
        >
          {!isCurrent(col) && (
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: col.accent,
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {col.name}
          </span>
        </span>
        {!isCurrent(col) &&
          (typeof col.descriptor === "string" ? (
            <span
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {col.descriptor}
            </span>
          ) : (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--ink-4)",
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {col.descriptor.names.length > 0
                ? `${col.descriptor.names.join(" · ")} · ${col.descriptor.count} items`
                : `${col.descriptor.count} items`}
            </span>
          ))}
        {isCurrent(col) && (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--ink-4)", lineHeight: 1.35 }}
          >
            Active flask · live
          </span>
        )}
      </div>
    </div>
  );
}

function SectionRow({ label, cols }: { label: string; cols: Col[] }) {
  return (
    <div
      className="cmp-section"
      style={{
        gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${cols.length}, minmax(110px, 1fr))`,
      }}
    >
      <div className="cmp-section-label">{label}</div>
      {cols.map((col, i) => (
        <div
          key={i}
          className={`cmp-section-cell ${isCurrent(col) ? "current" : ""}`}
        />
      ))}
    </div>
  );
}

function MetricRow({
  label,
  sublabel,
  cols,
  values,
  currentValue,
  prefix = "",
}: {
  label: string;
  sublabel?: string;
  cols: Col[];
  values: (number | null | undefined)[];
  currentValue: number | null;
  prefix?: string;
}) {
  return (
    <div
      className="cmp-row"
      style={{
        gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${cols.length}, minmax(110px, 1fr))`,
      }}
    >
      <div className="cmp-row-label metric">
        <span
          className="mono"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--ink-2)",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </span>
        {sublabel && (
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: "var(--ink-4)",
              letterSpacing: "0.04em",
            }}
          >
            {sublabel}
          </span>
        )}
      </div>
      {cols.map((col, i) => {
        const v = values[i];
        const present = v !== null && v !== undefined && !Number.isNaN(v);
        const diff =
          !isCurrent(col) && present && currentValue !== null
            ? (v as number) - currentValue
            : null;
        // Loss and Cost / 100g are both "lower is better".
        const better = diff !== null && diff < 0;
        const showDiff = diff !== null && Math.abs(diff) > 0.005;
        const color = showDiff
          ? better
            ? "#2f8a4d"
            : "#b46a18"
          : "var(--ink-4)";
        const sign = diff !== null && diff > 0 ? "+" : "";
        return (
          <div
            key={i}
            className={`cmp-cell metric ${isCurrent(col) ? "current" : ""}`}
          >
            {isCurrent(col) ? (
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--ink-3)",
                  fontWeight: 400,
                }}
              >
                {present
                  ? `${prefix}${(v as number).toFixed(2)}`
                  : "—"}
              </span>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto auto",
                  columnGap: 12,
                  alignItems: "baseline",
                  justifyItems: "start",
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--ink-3)",
                    fontWeight: 400,
                  }}
                >
                  {present
                    ? `${prefix}${(v as number).toFixed(2)}`
                    : "—"}
                </span>
                {showDiff && diff !== null ? (
                  <span
                    className="mono"
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color,
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 48,
                    }}
                  >
                    {sign}
                    {prefix}
                    {diff.toFixed(2)}
                  </span>
                ) : (
                  <span style={{ minWidth: 48, display: "inline-block" }} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function IngredientRow({
  compound,
  cols,
  ppms,
}: {
  compound: CompoundLibEntry;
  cols: Col[];
  ppms: (number | undefined)[];
}) {
  const isSub = compound.kind === "substance";
  return (
    <div
      className="cmp-row"
      style={{
        gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${cols.length}, minmax(110px, 1fr))`,
      }}
    >
      <div className="cmp-row-label">
        <div
          className="cmp-fam-stripe"
          style={{
            width: isSub ? 7 : 4,
            height: 22,
            gap: isSub ? 1 : 0,
          }}
        >
          <div
            className="cmp-fam-stripe-half"
            style={{
              flex: 1,
              background: `var(--fam-${compound.family})`,
            }}
          />
          {isSub && (
            <div
              className="cmp-fam-stripe-half"
              style={{
                flex: 1,
                background: `var(--fam-${compound.family})`,
                opacity: 0.45,
              }}
            />
          )}
        </div>
        <div
          className="vstack"
          style={{ minWidth: 0, lineHeight: 1.2 }}
        >
          <span className={`cmp-name${isSub ? " substance" : ""}`}>
            {compound.name}
          </span>
          <span className="cmp-name-cas">{compound.cas}</span>
        </div>
      </div>

      {cols.map((col, i) => {
        const ppm = ppms[i];
        const present = ppm !== undefined && ppm !== null;
        const currIdx = cols.findIndex(isCurrent);
        const currPpm = currIdx >= 0 ? ppms[currIdx] : undefined;
        const diff =
          !isCurrent(col) &&
          present &&
          currPpm !== undefined &&
          currPpm !== null
            ? (ppm as number) - currPpm
            : null;
        const showDiff = diff !== null && Math.abs(diff) > 0.005;

        return (
          <div
            key={i}
            className={`cmp-cell ${isCurrent(col) ? "current" : ""}`}
          >
            {!present ? (
              <span
                className="mono"
                title="Not in this formulation"
                style={{
                  fontSize: 16,
                  color: "var(--ink-4)",
                  fontWeight: 400,
                  lineHeight: 1,
                }}
              >
                —
              </span>
            ) : isCurrent(col) ? (
              <div
                className="hstack"
                style={{ alignItems: "baseline", gap: 4 }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--ink-3)",
                    fontWeight: 400,
                  }}
                >
                  {formatPpm(ppm as number)}
                </span>
                <span
                  className="mono"
                  style={{ fontSize: 9, color: "var(--ink-4)" }}
                >
                  ppm
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto auto",
                  columnGap: 12,
                  alignItems: "baseline",
                  justifyItems: "start",
                }}
              >
                <div
                  className="hstack"
                  style={{ alignItems: "baseline", gap: 4 }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 12,
                      fontVariantNumeric: "tabular-nums",
                      color: "var(--ink-3)",
                      fontWeight: 400,
                    }}
                  >
                    {formatPpm(ppm as number)}
                  </span>
                  <span
                    className="mono"
                    style={{ fontSize: 9, color: "var(--ink-4)" }}
                  >
                    ppm
                  </span>
                </div>
                {showDiff && diff !== null ? (
                  <span
                    className="mono"
                    title="Difference vs Current Flask"
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: diff > 0 ? "#2f8a4d" : "#b46a18",
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 48,
                    }}
                  >
                    {diff > 0 ? "+" : ""}
                    {formatDiff(diff)}
                  </span>
                ) : diff === null ? (
                  <span
                    className="mono"
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#2f8a4d",
                      minWidth: 48,
                    }}
                  >
                    + new
                  </span>
                ) : (
                  <span style={{ minWidth: 48, display: "inline-block" }} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Public component ─────────────────────────────────────────────────
type ComparePanelProps = {
  currentName: string;
  currentFormulation: FormulationEntry[];
  currentLoss: number | null;
  currentCost: CostBreakdown | null;
  selected: CompareFormula[];
  computeStats: ComputeCompareStats;
  catalog: CompareCatalog;
  onToggle: (f: CompareFormula) => void;
  onClear: () => void;
  onClearSlot: (id: string) => void;
  /** Forwarded to the header's <CompareControl> so the popover can show
   *  "Loading…" copy in the similar/AI sections while suggestions fetch. */
  suggestionsLoading?: boolean;
};

export function ComparePanel({
  currentName,
  currentFormulation,
  currentLoss,
  currentCost,
  selected,
  computeStats,
  catalog,
  onToggle,
  onClear,
  onClearSlot,
  suggestionsLoading,
}: ComparePanelProps) {
  // Column ordering per spec:
  //   1 selected → [Current, selected[0]]
  //   2 selected → [selected[1], Current, selected[0]]
  // (latest pick goes to the LEFT of Current; first pick stays on the right.)
  const cols = useMemo<Col[]>(() => {
    const cur: CurrentCol = {
      kind: "current",
      name: currentName,
      formulation: currentFormulation,
    };
    const altOf = (s: CompareFormula, pickIdx: number): AltCol => ({
      kind: "alt",
      id: s.id,
      name: s.name,
      descriptor: s.descriptor,
      source: s.source,
      formulation: s.formulation,
      accent: CMP_COLORS[pickIdx] ?? CMP_COLORS[0],
    });
    if (selected.length === 1) return [cur, altOf(selected[0], 0)];
    if (selected.length >= 2)
      return [altOf(selected[1], 1), cur, altOf(selected[0], 0)];
    return [cur];
  }, [selected, currentName, currentFormulation]);

  // Build ingredient rows — common (in current flask) sorted by current ppm
  // desc, then added-in-alts sorted by max ppm across selecteds.
  const rows = useMemo(() => {
    const currentMap = new Map(
      currentFormulation.map((f) => [f.compoundId, f.ppm])
    );
    const altMaps = selected.map(
      (s) => new Map(s.formulation.map((f) => [f.compoundId, f.ppm]))
    );

    const commonIds = [...currentFormulation]
      .sort((a, b) => b.ppm - a.ppm)
      .map((f) => f.compoundId);

    const addedSet = new Set<string>();
    for (const s of selected) {
      for (const f of s.formulation) {
        if (!currentMap.has(f.compoundId)) addedSet.add(f.compoundId);
      }
    }
    const addedIds = [...addedSet].sort((a, b) => {
      const aMax = Math.max(0, ...altMaps.map((m) => m.get(a) || 0));
      const bMax = Math.max(0, ...altMaps.map((m) => m.get(b) || 0));
      return bMax - aMax;
    });

    const ppmsFor = (compoundId: string): (number | undefined)[] =>
      cols.map((col) => {
        if (isCurrent(col)) return currentMap.get(compoundId);
        const idx = selected.findIndex((s) => s.id === col.id);
        return idx >= 0 ? altMaps[idx].get(compoundId) : undefined;
      });

    type Row = {
      id: string;
      compound: CompoundLibEntry;
      ppms: (number | undefined)[];
    };
    const buildRows = (ids: string[]): Row[] =>
      ids
        .map((id): Row | null => {
          const compound = compoundById(id);
          if (!compound) return null;
          return { id, compound, ppms: ppmsFor(id) };
        })
        .filter((r): r is Row => r !== null);

    return { common: buildRows(commonIds), added: buildRows(addedIds) };
  }, [cols, selected, currentFormulation]);

  const lossVals = cols.map((col) =>
    isCurrent(col) ? currentLoss : computeStats(col.formulation).loss
  );
  const costVals = cols.map((col) => {
    if (isCurrent(col))
      return currentCost ? currentCost.usdPer100g : null;
    const c = computeStats(col.formulation).cost;
    return c ? c.usdPer100g : null;
  });

  const gridCols = `minmax(180px, 1.4fr) repeat(${cols.length}, minmax(110px, 1fr))`;

  return (
    <div className="panel cmp-panel" data-screen-label="Compare">
      <div className="cmp-panel-head">
        <div className="hstack gap-2">
          <Icon name="compare" size={14} />
          <span className="label">Comparing formulae</span>
          <span className="cmp-panel-pill">{cols.length} columns</span>
        </div>
        <div className="hstack gap-2">
          <CompareControl
            catalog={catalog}
            selected={selected}
            onToggle={onToggle}
            onClear={onClear}
            computeStats={computeStats}
            currentFormulation={currentFormulation}
            suggestionsLoading={suggestionsLoading}
            active
          />
          <button
            type="button"
            className="btn xs ghost"
            onClick={onClear}
            title="Exit comparison"
          >
            <Icon name="x" size={12} />
            Close
          </button>
        </div>
      </div>

      {/* Column-aligned header strip */}
      <div className="cmp-headstrip" style={{ gridTemplateColumns: gridCols }}>
        <div className="cmp-headstrip-gutter" />
        {cols.map((col) => (
          <ColumnHeader
            key={isCurrent(col) ? "cur" : col.id}
            col={col}
            onClear={
              !isCurrent(col) ? () => onClearSlot(col.id) : undefined
            }
          />
        ))}
      </div>

      <div className="cmp-panel-body">
        <SectionRow label="Metrics" cols={cols} />
        <MetricRow
          label="Loss"
          sublabel="Target ↔ flask"
          cols={cols}
          values={lossVals}
          currentValue={currentLoss}
        />
        <MetricRow
          label="Cost / 100g"
          sublabel="Per 100 g of formulation"
          cols={cols}
          values={costVals}
          currentValue={currentCost ? currentCost.usdPer100g : null}
          prefix="$"
        />

        <SectionRow label="Ingredient" cols={cols} />
        {rows.common.map((r) => (
          <IngredientRow
            key={r.id}
            compound={r.compound}
            cols={cols}
            ppms={r.ppms}
          />
        ))}
        {rows.added.map((r) => (
          <IngredientRow
            key={r.id}
            compound={r.compound}
            cols={cols}
            ppms={r.ppms}
          />
        ))}

        {rows.common.length === 0 && rows.added.length === 0 && (
          <div className="cmp-empty">No ingredients to compare yet.</div>
        )}
      </div>

      <div className="cmp-panel-foot">
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "var(--ink-4)",
            letterSpacing: "0.04em",
          }}
        >
          Δ shown vs Current Flask · ppm values not editable in compare view
        </span>
        <span
          className="mono"
          style={{ fontSize: 9.5, color: "var(--ink-4)" }}
        >
          {selected.length === 2 ? "2 of 2" : "1 of 2"} alternatives shown
        </span>
      </div>
    </div>
  );
}

